import { useMemo, useState } from "react";
import { ToolContainer } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogToolUsage } from "@/hooks/useLogToolUsage";
import { analyzeCron, formatRun } from "./cronUtils";
import { NoticeBox } from "@/components/NoticeBox";

const FIELD_OPTIONS: { label: string; hint: string; options: string[] }[] = [
  { label: "Minute", hint: "0-59", options: ["*", "0", "5", "15", "30", "*/5", "*/10", "*/15", "*/30"] },
  { label: "Hour", hint: "0-23", options: ["*", "0", "6", "9", "12", "18", "*/2", "*/6", "9-17"] },
  { label: "Day of month", hint: "1-31", options: ["*", "1", "15", "1,15", "*/2"] },
  { label: "Month", hint: "1-12 or JAN-DEC", options: ["*", "1", "6", "12", "1-6", "*/3"] },
  { label: "Day of week", hint: "0-7 or SUN-SAT", options: ["*", "1-5", "MON-FRI", "0,6", "1", "0"] },
];

const PRESETS = [
  { label: "Every minute", expr: "* * * * *" },
  { label: "Every hour", expr: "0 * * * *" },
  { label: "Every day at midnight", expr: "0 0 * * *" },
  { label: "Every Monday at 9am", expr: "0 9 * * 1" },
  { label: "First day of every month", expr: "0 0 1 * *" },
  { label: "Every 15 minutes", expr: "*/15 * * * *" },
];

const TOOL_INFO_CONTENT = {
  what: `Builds and explains cron expressions — the schedule syntax used to automate recurring tasks on a server — translating between the cryptic expression and a plain-English description.`,
  how: `Type a cron expression to see its plain-English explanation, or start from one of the common presets and adjust it to your schedule.`,
  tip: `Standard cron has 5 fields; Quartz and AWS EventBridge use 6–7 with different rules. Double-check the day-of-week field specifically before deploying a schedule — it's the field most commonly mixed up, since some systems count Sunday as 0 and others as 7.`,
};

export default function CronBuilder() {
  const [expr, setExpr] = useState("0 9 * * 1-5");

  const browserTz = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC", []);
  const [tz, setTz] = useState(browserTz);
  const tzOptions = browserTz === "UTC" ? ["UTC"] : [browserTz, "UTC"];

  const result = useMemo(() => analyzeCron(expr, tz), [expr, tz]);
  const explanation = result.ok ? result.description ?? "" : "";

  const fields = expr.trim().split(/\s+/);
  const setField = (i: number, v: string) => {
    const f = fields.length === 5 ? [...fields] : ["*", "*", "*", "*", "*"];
    f[i] = v.trim() || "*";
    setExpr(f.join(" "));
  };

  const { blocked } = useLogToolUsage(explanation);


  return (
    <ToolContainer>
      <Label htmlFor="cron-expr" className="mb-2 block">Cron Expression</Label>
      <Input
        id="cron-expr"
        value={expr}
        onChange={e => setExpr(e.target.value)}
        className="font-mono text-lg"
      />
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-3">
        {FIELD_OPTIONS.map((f, i) => (
          <div key={f.label}>
            <Label className="text-xs text-muted-foreground mb-1 block">
              {f.label} <span className="opacity-70">({f.hint})</span>
            </Label>
            <Input
              list={`cron-f${i}`}
              value={fields.length === 5 ? fields[i] : ""}
              onChange={e => setField(i, e.target.value)}
              placeholder="*"
              className="font-mono"
            />
            <datalist id={`cron-f${i}`}>
              {f.options.map(o => (
                <option key={o} value={o} />
              ))}
            </datalist>
          </div>
        ))}
      </div>

      {!result.ok && result.error && (
        <Card className="mt-4 p-4 border-destructive/50">
          <p className="text-sm text-destructive font-medium">Invalid cron expression</p>
          <p className="text-sm mt-1">{result.error}</p>
        </Card>
      )}

      {result.ok && (
        <Card className="mt-4 p-4">
          <p className="text-sm text-muted-foreground mb-1">In plain English</p>
          <p className="font-medium">{blocked ? "— blocked at today's usage limit —" : explanation}</p>
        </Card>
      )}

      {result.ok &&
        result.warnings.map(w => (
          <NoticeBox key={w} className="mt-3 text-sm">
            ⚠ {w}
          </NoticeBox>
        ))}

      {result.ok && result.nextRuns.length > 0 && !blocked && (
        <Card className="mt-4 p-4">
          <div className="flex items-center justify-between gap-3 mb-2">
            <p className="text-sm text-muted-foreground">Next {result.nextRuns.length} runs in</p>
            <select
              value={tz}
              onChange={e => setTz(e.target.value)}
              className="text-sm border border-border rounded-md bg-background px-2 py-1"
              aria-label="Time zone"
            >
              {tzOptions.map(z => (
                <option key={z} value={z}>
                  {z}
                </option>
              ))}
            </select>
          </div>
          <ol className="space-y-1 font-mono text-sm">
            {result.nextRuns.map(d => (
              <li key={d.toISOString()}>{formatRun(d, tz)}</li>
            ))}
          </ol>
          <p className="mt-3 text-xs text-muted-foreground">The schedule is read as clock time in the selected zone, so 09:00 means 09:00 there. Servers, GitHub Actions and Kubernetes CronJobs usually run cron in UTC, so pick UTC to see their runs; the times shown are not converted between zones.</p>
        </Card>
      )}

      <div className="mt-6">
        <p className="text-sm font-medium mb-2">Common presets</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {PRESETS.map(p => (
            <button
              key={p.expr}
              type="button"
              onClick={() => setExpr(p.expr)}
              className="text-left px-3 py-2 rounded-lg border border-border bg-card hover:border-primary/30 text-sm"
            >
              <p className="font-medium">{p.label}</p>
              <p className="text-xs text-muted-foreground font-mono">
                {p.expr}
              </p>
            </button>
          ))}
        </div>
      </div>
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
